import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Loading } from '@/components/Loading';
import { TicketHeader } from './TicketHeader';
import { EnhancedConversationPanel } from './EnhancedConversationPanel';
import { PropertiesPanel } from './PropertiesPanel';
import { ToolsPanel } from './ToolsPanel';
import { ticketService } from '@/services/ticketService';
import { TicketStatus, TicketPriority, UserRole } from '@/types/api';
import type { Ticket } from '@/types/api';

/**
 * Professional three-column ticket details layout
 * Based on Freshdesk patterns with Webcoda design system integration
 */
export const TicketDetailsLayout: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuth();
  
  // Layout state
  const [isLoading, setIsLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toolsPanelVisible, setToolsPanelVisible] = useState(true);
  
  // Mobile/tablet responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeTab, setActiveTab] = useState<'conversation' | 'properties' | 'tools'>('conversation');
  
  // Collaboration state
  const [liveCollaborators, setLiveCollaborators] = useState<string[]>([]);
  
  // Conversation refresh state - changing this key forces conversation panel to reload
  const [conversationRefreshKey, setConversationRefreshKey] = useState(0);
  
  // Check responsive breakpoints
  useEffect(() => {
    const checkBreakpoints = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1200);
      
      // Auto-collapse panels on smaller screens
      if (width < 1200) {
        setToolsPanelVisible(false);
      } else {
        setToolsPanelVisible(true);
      }
    };
    
    checkBreakpoints();
    window.addEventListener('resize', checkBreakpoints);
    
    return () => window.removeEventListener('resize', checkBreakpoints);
  }, []);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in inputs or rich text editors
      const target = event.target as HTMLElement;
      
      if (target instanceof HTMLInputElement || 
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target.isContentEditable ||
          target.closest('[contenteditable]') ||
          target.closest('[data-slate-editor]') ||
          target.closest('.slate-editor') ||
          target.closest('[role="textbox"]')) {
        return;
      }
      
      switch (event.key.toLowerCase()) {
        case 'j':
          event.preventDefault();
          navigateToAdjacentTicket('previous');
          break;
        case 'k':
          event.preventDefault();
          navigateToAdjacentTicket('next');
          break;
        case 'r':
          event.preventDefault();
          focusReplyEditor();
          break;
        case 'e':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            focusTicketProperties();
          }
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [ticket]);
  
  // Load ticket data
  useEffect(() => {
    const loadTicket = async () => {
      if (!ticketId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Load ticket from API - detect if ticketId is GUID or ticket number
        const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ticketId);
        const response = isGuid 
          ? await ticketService.getTicket(ticketId)
          : await ticketService.getTicketByNumber(ticketId);
        
        if (response.success && response.data) {
          setTicket(response.data);
        } else {
          throw new Error(response.message || 'Failed to load ticket');
        }
        
      } catch (err) {
        console.error('Error loading ticket:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to load ticket details');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTicket();
  }, [ticketId, user?.id, hasAnyRole]);
  
  // Permission checks
  const canViewTicket = useCallback(() => {
    if (!ticket) return false;
    if (hasAnyRole([UserRole.Admin, UserRole.Agent])) return true;
    if (user?.role === UserRole.EndUser && ticket.requester?.id === user.id) return true;
    return false;
  }, [ticket, user, hasAnyRole]);
  
  const canEditTicket = useCallback(() => {
    return hasAnyRole([UserRole.Admin, UserRole.Agent]);
  }, [hasAnyRole]);
  
  const canReply = useCallback(() => {
    return canViewTicket();
  }, [canViewTicket]);
  
  // Navigation functions
  const navigateToAdjacentTicket = async (direction: 'previous' | 'next') => {
    if (!ticket) return;
    
    try {
      // Get navigation context from location state or use defaults
      const navParams = location.state?.navParams || {};
      const adjacentResponse = await ticketService.getAdjacentTickets(ticket.id, navParams);
      
      if (adjacentResponse.success && adjacentResponse.data) {
        const targetTicket = direction === 'previous' 
          ? adjacentResponse.data.previous 
          : adjacentResponse.data.next;
        
        if (targetTicket) {
          navigate(`/tickets/${targetTicket.ticketNumber || targetTicket.id}`, {
            state: { 
              from: location.state?.from || location.pathname,
              context: location.state?.context,
              returnState: location.state?.returnState,
              navParams // Preserve navigation context
            }
          });
        }
      }
    } catch (error) {
      console.error(`Failed to navigate to ${direction} ticket:`, error);
    }
  };
  
  const focusReplyEditor = () => {
    const replyEditor = document.querySelector('[data-reply-editor]') as HTMLElement;
    if (replyEditor) {
      replyEditor.focus();
    }
  };
  
  const focusTicketProperties = () => {
    if (isMobile) {
      setActiveTab('properties');
    } else {
      const propertiesPanel = document.querySelector('[data-properties-panel]') as HTMLElement;
      if (propertiesPanel) {
        propertiesPanel.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  // Ticket update handlers
  const handleTicketUpdate = (updates: Partial<Ticket>) => {
    if (!ticket) return;
    setTicket({ ...ticket, ...updates });
  };
  
  const handleStatusChange = (newStatus: TicketStatus) => {
    handleTicketUpdate({ status: newStatus });
  };
  
  const handlePriorityChange = (newPriority: TicketPriority) => {
    handleTicketUpdate({ priority: newPriority });
  };
  
  // Conversation refresh callback for ToolsPanel quick notes
  const handleConversationUpdate = useCallback(() => {
    console.log('🔄 Refreshing conversations from ToolsPanel quick note');
    setConversationRefreshKey(prev => prev + 1);
  }, []);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Loading ticket details..." />
      </div>
    );
  }
  
  // Error state
  if (error || !ticket) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Ticket not found'}
          </h2>
          <p className="text-gray-600 mb-4">
            The ticket you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button variant="primary" onClick={() => navigate('/tickets')}>
            Back to Tickets
          </Button>
        </div>
      </div>
    );
  }
  
  // Permission check
  if (!canViewTicket()) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-amber-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to view this ticket.
          </p>
          <Button variant="primary" onClick={() => navigate('/tickets')}>
            Back to Tickets
          </Button>
        </div>
      </div>
    );
  }
  
  // Mobile layout (single column with tabs)
  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        {/* Mobile Header */}
        <TicketHeader 
          ticket={ticket}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onNavigatePrevious={() => navigateToAdjacentTicket('previous')}
          onNavigateNext={() => navigateToAdjacentTicket('next')}
          canEdit={canEditTicket()}
          isMobile={true}
          onTicketUpdate={handleTicketUpdate}
        />
        
        {/* Mobile Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-white">
          <button
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 focus:outline-none',
              activeTab === 'conversation'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
            onClick={() => setActiveTab('conversation')}
          >
            Conversation
          </button>
          <button
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 focus:outline-none',
              activeTab === 'properties'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
            onClick={() => setActiveTab('properties')}
          >
            Properties
          </button>
          <button
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 focus:outline-none',
              activeTab === 'tools'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
            onClick={() => setActiveTab('tools')}
          >
            Tools
          </button>
        </div>
        
        {/* Mobile Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'conversation' && (
            <EnhancedConversationPanel
              key={`conversation-mobile-${conversationRefreshKey}`}
              ticket={ticket}
              onTicketUpdate={handleTicketUpdate}
              canReply={canReply()}
              canEdit={canEditTicket()}
              className="h-full"
            />
          )}
          {activeTab === 'properties' && (
            <PropertiesPanel
              ticket={ticket}
              onTicketUpdate={handleTicketUpdate}
              canEdit={canEditTicket()}
              className="h-full overflow-auto"
            />
          )}
          {activeTab === 'tools' && (
            <ToolsPanel
              ticket={ticket}
              onTicketUpdate={handleTicketUpdate}
              onConversationUpdate={handleConversationUpdate}
              canEdit={canEditTicket()}
              liveCollaborators={liveCollaborators}
              className="h-full overflow-auto"
            />
          )}
        </div>
      </div>
    );
  }
  
  // Desktop/Tablet layout
  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Desktop Header */}
      <TicketHeader 
        ticket={ticket}
        onStatusChange={handleStatusChange}
        onPriorityChange={handlePriorityChange}
        onNavigatePrevious={() => navigateToAdjacentTicket('previous')}
        onNavigateNext={() => navigateToAdjacentTicket('next')}
        canEdit={canEditTicket()}
        isMobile={false}
        onTicketUpdate={handleTicketUpdate}
      />
      
      {/* Three-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Conversation (60% width) */}
        <div className={clsx(
          'flex-1 flex flex-col bg-white border-r border-gray-200',
          isTablet ? 'min-w-0' : 'w-3/5'
        )}>
          <EnhancedConversationPanel
            key={`conversation-desktop-${conversationRefreshKey}`}
            ticket={ticket}
            onTicketUpdate={handleTicketUpdate}
            canReply={canReply()}
            canEdit={canEditTicket()}
            className="h-full"
          />
        </div>
        
        {/* Right Panel: Properties (25% width, hidden on tablet if tools visible) */}
        <div className={clsx(
          'flex flex-col bg-white border-r border-gray-200',
          isTablet ? 'w-2/5 min-w-80' : 'w-1/4 min-w-72',
          isTablet && toolsPanelVisible && 'hidden'
        )}>
          <PropertiesPanel
            ticket={ticket}
            onTicketUpdate={handleTicketUpdate}
            canEdit={canEditTicket()}
            data-properties-panel
            className="h-full"
          />
        </div>
        
        {/* Far Right Panel: Apps & Tools (15% width) */}
        {toolsPanelVisible && (
          <div className={clsx(
            'flex flex-col bg-white',
            isTablet ? 'w-2/5 min-w-80' : 'w-1/6 min-w-64'
          )}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Apps & Tools</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToolsPanelVisible(false)}
                className="p-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
            <ToolsPanel
              ticket={ticket}
              onTicketUpdate={handleTicketUpdate}
              onConversationUpdate={handleConversationUpdate}
              canEdit={canEditTicket()}
              liveCollaborators={liveCollaborators}
              className="flex-1"
            />
          </div>
        )}
      </div>
      
      {/* Toggle Tools Panel Button (when collapsed) */}
      {!toolsPanelVisible && !isTablet && (
        <button
          className="fixed right-4 top-1/2 transform -translate-y-1/2 bg-white border border-gray-200 rounded-l-md px-2 py-3 shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          onClick={() => setToolsPanelVisible(true)}
          title="Show Apps & Tools"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};